import React, { useState, useEffect, useRef } from 'react'
import Container from '../../Components/Container'
import DataTable from 'react-data-table-component'
import toast, { Toaster } from 'react-hot-toast'
import { FaPlus, FaEdit, FaTrash, FaTags, FaSearch, FaTimes } from 'react-icons/fa'
import { motion, AnimatePresence } from 'framer-motion'

const readJson = (res) => {
    if (!res.ok) {
        throw new Error('Sunucu yanıt vermedi')
    }
    return res.json()
}

const isMutationError = (data) => {
    if (data == null || typeof data !== 'object') return false
    const st = data.status
    if (st === 400) return true
    if (typeof st === 'number' && st >= 400) return true
    return false
}

const Category = () => {
    const [open, setOpen] = useState(false)
    const [name, setName] = useState('')
    const [data, setData] = useState([])
    const [update, setUpdate] = useState(false)
    const [id, setId] = useState(null)
    const [filterText, setFilterText] = useState('')
    const [loading, setLoading] = useState(false)
    const [submitting, setSubmitting] = useState(false)
    const nameInputRef = useRef(null)

    const openCreateModal = () => {
        setUpdate(false)
        setId(null)
        setName('')
        setOpen(true)
    }

    const handleSubmit = () => {
        if (!name.trim()) {
            toast.error('Kategori adı boş olamaz')
            return
        }

        setSubmitting(true)
        const formdata = new FormData()
        formdata.append('action', update ? 'update' : 'insert')
        formdata.append('name', name.trim())
        if (update) formdata.append('id', id)

        toast.promise(
            fetch(process.env.REACT_APP_BASE_URL + 'categories.php', {
                method: 'POST',
                body: formdata
            })
                .then(readJson)
                .then((data) => {
                    if (isMutationError(data)) {
                        throw new Error(data.message || 'İşlem reddedildi')
                    }
                    fetchData()
                    handleClose()
                    return data
                }),
            {
                loading: update ? 'Güncelleniyor...' : 'Kaydediliyor...',
                success: update ? 'Kategori güncellendi!' : 'Yeni kategori eklendi!',
                error: (err) => err.message
            }
        ).finally(() => setSubmitting(false))
    }

    const handleClose = () => {
        setOpen(false)
        setName('')
        setUpdate(false)
        setId(null)
    }

    const fetchData = () => {
        setLoading(true)
        const formdata = new FormData()
        formdata.append('action', 'select')
        
        fetch(process.env.REACT_APP_BASE_URL + 'categories.php', {
            method: 'POST',
            body: formdata
        })
            .then(readJson)
            .then((data) => {
                if (data != null && data.status === 400) {
                    toast.error(data.message || 'Liste alınamadı')
                    setData([])
                    return
                }
                setData(Array.isArray(data) ? data : [])
            })
            .catch(() => toast.error('Veriler yüklenirken bir hata oluştu'))
            .finally(() => setLoading(false))
    }

    const handleDelete = (row) => {
        toast(
            (t) => (
                <div className='flex flex-col gap-4'>
                    <p className='text-sm leading-relaxed text-slate-700'>
                        <span className='font-semibold text-slate-900'>{row.name}</span>{' '}
                        kategorisini silmek istediğinize emin misiniz?
                    </p>
                    <div className='flex justify-end gap-2'>
                        <button
                            type='button'
                            className='rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50'
                            onClick={() => toast.dismiss(t.id)}
                        >
                            İptal
                        </button>
                        <button
                            type='button'
                            className='rounded-lg bg-red-600 px-3 py-2 text-sm font-semibold text-white transition hover:bg-red-700'
                            onClick={() => {
                                confirmDelete(row)
                                toast.dismiss(t.id)
                            }}
                        >
                            Sil
                        </button>
                    </div>
                </div>
            ),
            { duration: 8000 }
        )
    }

    const confirmDelete = (row) => {
        setLoading(true)
        const formdata = new FormData()
        formdata.append('action', 'delete')
        formdata.append('id', row.id)
        formdata.append('cat', row.name)

        toast.promise(
            fetch(process.env.REACT_APP_BASE_URL + 'categories.php', {
                method: 'POST',
                body: formdata
            })
                .then(readJson)
                .then((data) => {
                    if (isMutationError(data)) {
                        throw new Error(data.message || 'Silinemedi')
                    }
                    fetchData()
                    return data
                }),
            {
                loading: 'Siliniyor...',
                success: 'Kategori başarıyla silindi!',
                error: (err) => err.message
            }
        ).finally(() => setLoading(false))
    }

    const handleUpdate = (row) => {
        setOpen(true)
        setUpdate(true)
        setName(row.name)
        setId(row.id)
    }

    useEffect(() => {
        fetchData()
    }, [])

    useEffect(() => {
        if (!open) return
        const raf = requestAnimationFrame(() => nameInputRef.current?.focus())
        const onKey = (e) => {
            if (e.key === 'Escape' && !submitting) handleClose()
        }
        document.addEventListener('keydown', onKey)
        const prevOverflow = document.body.style.overflow
        document.body.style.overflow = 'hidden'
        return () => {
            cancelAnimationFrame(raf)
            document.removeEventListener('keydown', onKey)
            document.body.style.overflow = prevOverflow
        }
    }, [open, submitting])

    const filteredData = (Array.isArray(data) ? data : []).filter((item) =>
        (item.name ?? '').toLowerCase().includes(filterText.toLowerCase())
    )

    const customStyles = {
        table: {
            style: {
                backgroundColor: 'transparent',
            },
        },
        headRow: {
            style: {
                backgroundColor: '#f1f5f9',
                borderBottomWidth: '1px',
                borderBottomColor: '#e2e8f0',
                borderTopLeftRadius: '1rem',
                borderTopRightRadius: '1rem',
                minHeight: '3rem',
            },
        },
        headCells: {
            style: {
                color: '#475569',
                fontSize: '0.6875rem',
                fontWeight: '700',
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
                paddingTop: '1rem',
                paddingBottom: '1rem',
                paddingLeft: '1.25rem',
                paddingRight: '1.25rem',
            },
        },
        rows: {
            style: {
                color: '#0f172a',
                fontSize: '0.875rem',
                borderBottomColor: '#f1f5f9',
            },
        },
        cells: {
            style: {
                paddingTop: '1rem',
                paddingBottom: '1rem',
                paddingLeft: '1.25rem',
                paddingRight: '1.25rem',
            },
        },
    }

    const columns = [
        {
            name: 'No',
            selector: row => row.id,
            sortable: true,
            width: '100px'
        },
        {
            name: 'Kategori',
            selector: row => row.name,
            sortable: true,
            grow: 2
        },
        {
            name: 'İşlemler',
            cell: (row) => (
                <div className='flex items-center gap-1'>
                    <button
                        type='button'
                        onClick={() => handleUpdate(row)}
                        className='rounded-xl p-2.5 text-indigo-600 transition hover:bg-indigo-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2'
                        title='Düzenle'
                    >
                        <FaEdit size={17} />
                    </button>
                    <button
                        type='button'
                        onClick={() => handleDelete(row)}
                        className='rounded-xl p-2.5 text-red-600 transition hover:bg-red-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2'
                        title='Sil'
                    >
                        <FaTrash size={17} />
                    </button>
                </div>
            ),
            width: '140px',
        }
    ]

    const inputClass =
        'w-full rounded-xl border border-slate-200 bg-slate-50/80 px-3.5 py-2.5 text-sm text-slate-900 shadow-sm placeholder:text-slate-400 focus:border-indigo-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20'

    return (
        <Container padding bg='#f8fafc'>
            <Toaster
                position='top-right'
                toastOptions={{
                    className: 'text-sm font-medium shadow-lg',
                }}
            />

            <AnimatePresence>
                {open && (
                    <motion.div
                        key='category-modal'
                        role='presentation'
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className='fixed inset-0 z-50 flex items-end justify-center bg-slate-900/40 p-4 backdrop-blur-sm sm:items-center'
                        onClick={() => !submitting && handleClose()}
                    >
                        <motion.div
                            role='dialog'
                            aria-modal='true'
                            aria-labelledby='category-modal-title'
                            initial={{ scale: 0.96, opacity: 0, y: 12 }}
                            animate={{ scale: 1, opacity: 1, y: 0 }}
                            exit={{ scale: 0.96, opacity: 0, y: 12 }}
                            transition={{
                                type: 'spring',
                                damping: 26,
                                stiffness: 320,
                            }}
                            className='w-full max-w-md overflow-hidden rounded-t-2xl border border-slate-200 bg-white shadow-2xl sm:rounded-2xl'
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className='border-b border-slate-100 px-6 py-5'>
                                <div className='flex items-start justify-between gap-3'>
                                    <h2
                                        id='category-modal-title'
                                        className='text-lg font-bold tracking-tight text-slate-900'
                                    >
                                        {update
                                            ? 'Kategori düzenle'
                                            : 'Yeni kategori'}
                                    </h2>
                                    <button
                                        type='button'
                                        onClick={() => !submitting && handleClose()}
                                        disabled={submitting}
                                        className='rounded-xl p-2 text-slate-500 transition hover:bg-slate-100 hover:text-slate-800 disabled:opacity-50'
                                        aria-label='Kapat'
                                    >
                                        <FaTimes size={18} />
                                    </button>
                                </div>
                                <p className='mt-2 text-sm leading-relaxed text-slate-600'>
                                    Kategori adı ürün ekranlarında ve raporlarda
                                    görünür.
                                </p>
                            </div>
                            <div className='space-y-4 px-6 py-6'>
                                <div>
                                    <label
                                        htmlFor='category-name-input'
                                        className='mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500'
                                    >
                                        Kategori adı
                                    </label>
                                    <input
                                        ref={nameInputRef}
                                        id='category-name-input'
                                        type='text'
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (
                                                e.key === 'Enter' &&
                                                !submitting
                                            ) {
                                                e.preventDefault()
                                                handleSubmit()
                                            }
                                        }}
                                        className={inputClass}
                                        placeholder='Örn. İçecek, Atıştırmalık…'
                                        disabled={submitting}
                                        autoComplete='off'
                                    />
                                </div>
                            </div>
                            <div className='flex justify-end gap-3 border-t border-slate-100 bg-slate-50/80 px-6 py-4'>
                                <button
                                    type='button'
                                    onClick={handleClose}
                                    className='rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:opacity-50'
                                    disabled={submitting}
                                >
                                    İptal
                                </button>
                                <button
                                    type='button'
                                    onClick={handleSubmit}
                                    className='inline-flex items-center justify-center gap-2 rounded-xl bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-lg shadow-indigo-500/25 transition hover:bg-indigo-700 disabled:pointer-events-none disabled:opacity-60'
                                    disabled={submitting}
                                >
                                    {submitting ? (
                                        <div className='h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent' />
                                    ) : update ? (
                                        <>
                                            <FaEdit size={16} />
                                            Güncelle
                                        </>
                                    ) : (
                                        <>
                                            <FaPlus size={16} />
                                            Ekle
                                        </>
                                    )}
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            <div className='mx-auto w-full max-w-[1400px] space-y-6 px-4 pb-16 pt-2 sm:px-6 lg:px-8'>
                <div className='rounded-2xl border border-slate-200/80 bg-white p-6 shadow-sm shadow-slate-200/50 sm:p-8'>
                    <div className='flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between'>
                        <div className='flex items-start gap-4'>
                            <div className='flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-indigo-500 to-violet-600 text-white shadow-md shadow-indigo-500/25'>
                                <FaTags className='text-xl' />
                            </div>
                            <div>
                                <h1 className='text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl'>
                                    Kategoriler
                                </h1>
                                <p className='mt-1 max-w-xl text-sm leading-relaxed text-slate-600'>
                                    Ürün gruplarınızı düzenleyin; liste ve arama
                                    ile hızlıca bulun.
                                </p>
                                <p className='mt-3 inline-flex rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700'>
                                    {filteredData.length} kayıt
                                    {filterText.trim()
                                        ? ' (filtrelendi)'
                                        : ''}
                                </p>
                            </div>
                        </div>
                        <div className='flex flex-col gap-3 sm:flex-row sm:items-center'>
                            <div className='relative min-w-[16rem]'>
                                <FaSearch className='pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400' />
                                <input
                                    type='text'
                                    value={filterText}
                                    onChange={(e) =>
                                        setFilterText(e.target.value)
                                    }
                                    placeholder='Kategori ara…'
                                    className={`${inputClass} pl-10`}
                                />
                            </div>
                            <button
                                type='button'
                                onClick={openCreateModal}
                                className='inline-flex items-center justify-center gap-2 rounded-xl bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-md shadow-indigo-500/25 transition hover:bg-indigo-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2'
                            >
                                <FaPlus size={16} />
                                Yeni kategori
                            </button>
                        </div>
                    </div>
                </div>

                <div className='overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-sm'>
                    <DataTable
                        columns={columns}
                        data={filteredData}
                        pagination
                        customStyles={customStyles}
                        progressPending={loading}
                        progressComponent={
                            <div className='flex flex-col items-center justify-center gap-3 py-20'>
                                <div className='h-10 w-10 animate-spin rounded-full border-2 border-indigo-600 border-t-transparent' />
                                <p className='text-sm font-medium text-slate-600'>
                                    Yükleniyor…
                                </p>
                            </div>
                        }
                        noDataComponent={
                            <div className='flex flex-col items-center justify-center px-6 py-16 text-center'>
                                <div className='mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100 text-slate-400'>
                                    <FaTags className='text-2xl' />
                                </div>
                                <p className='text-base font-semibold text-slate-800'>
                                    Henüz kategori yok
                                </p>
                                <p className='mt-2 max-w-sm text-sm text-slate-600'>
                                    Yeni kategori ekleyerek ürünlerinizi
                                    gruplandırmaya başlayın.
                                </p>
                                <button
                                    type='button'
                                    onClick={openCreateModal}
                                    className='mt-6 inline-flex items-center gap-2 rounded-xl bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-md transition hover:bg-indigo-700'
                                >
                                    <FaPlus size={14} />
                                    Kategori ekle
                                </button>
                            </div>
                        }
                    />
                </div>
            </div>
        </Container>
    )
}

export default Category